import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { MijnInterfaceCacheService } from '@ap/mijn-interface-cache';

import {
    Betaaltransactie,
    Betaalverzoek,
    BetaalverzoekTotalen,
    IsZakelijk,
    LijstVanBetaaltransacties,
    LijstVanBetaalverzoeken,
    Limiet,
    Permissies
} from './betaalverzoek.model';

@Injectable({ providedIn: 'root' })
export class BetaalverzoekResource {
    private readonly baseUrl: string = '/services/apic-betaalverzoek/api';

    public constructor(
        private readonly httpClient: HttpClient,
        private readonly mijnCacheService: MijnInterfaceCacheService
    ) {}

    public createBetaalverzoek(betaalverzoek: Betaalverzoek): Observable<HttpResponse<void>> {
        return this.httpClient.post<void>(`${this.baseUrl}/betaalverzoeken`, betaalverzoek, {
            observe: 'response',
            headers: this.getApiXHeader()
        });
    }

    public createAndGetBetaalverzoek(betaalverzoek: Betaalverzoek): Observable<Betaalverzoek> {
        return this.createBetaalverzoek(betaalverzoek).pipe(
            map((response) => response.headers.get('Location') ?? ''),
            switchMap((url: string) => this.getBetaalverzoekByUrl(url))
        );
    }

    public getBetaalverzoeken(): Observable<LijstVanBetaalverzoeken> {
        return this.httpClient.get<LijstVanBetaalverzoeken>(`${this.baseUrl}/betaalverzoeken`, { headers: this.getApiXHeader() });
    }

    public getBetaalverzoekenMetPaginering(
        cursor: string | undefined = undefined,
        betaalverzoekStatus: string | undefined = undefined,
        size: number | undefined = undefined,
        isRekeninghouder: boolean | undefined = undefined
    ): Observable<LijstVanBetaalverzoeken> {
        let params = new HttpParams();
        params = params.append('paginering_actief', true);
        if (cursor) {
            params = params.append('cursor', cursor);
        }
        if (betaalverzoekStatus) {
            params = params.append('betaalverzoek_status', betaalverzoekStatus);
        }
        if (isRekeninghouder === true || isRekeninghouder === false) {
            params = params.append('is_rekeninghouder', isRekeninghouder);
        }
        if (size) {
            params = params.append('size', size);
        }
        return this.httpClient.get<LijstVanBetaalverzoeken>(`${this.baseUrl}/betaalverzoeken`, {
            params: params,
            headers: this.getApiXHeader()
        });
    }

    public getBetaalverzoek(betaalverzoekId: string, autorisatieCode: string): Observable<Betaalverzoek> {
        return this.getBetaalverzoekByUrl(`${this.baseUrl}/betaalverzoeken/${betaalverzoekId}?autorisatie_code=${autorisatieCode}`);
    }

    public deleteBetaalverzoek(betaalverzoekId: string): Observable<HttpResponse<void>> {
        return this.httpClient.delete<void>(`${this.baseUrl}/betaalverzoeken/${betaalverzoekId}`, {
            observe: 'response',
            headers: this.getApiXHeader()
        });
    }

    public getBetaalverzoekTotalen(): Observable<BetaalverzoekTotalen> {
        return this.httpClient.get<BetaalverzoekTotalen>(`${this.baseUrl}/betaalverzoeken/totalen`, { headers: this.getApiXHeader() });
    }

    public createBetaaltransactie(betaaltransactie: Betaaltransactie): Observable<HttpResponse<void>> {
        return this.httpClient.post<void>(
            `${this.baseUrl}/betaalverzoeken/${betaaltransactie.betaalverzoek_id}/betaaltransacties`,
            betaaltransactie,
            { observe: 'response', headers: this.getApiXHeader() }
        );
    }

    public getBetaaltransacties(betaalverzoekId: string): Observable<LijstVanBetaaltransacties> {
        return this.httpClient.get<LijstVanBetaaltransacties>(`${this.baseUrl}/betaaltransacties?betaalverzoek_id=${betaalverzoekId}`, {
            headers: this.getApiXHeader()
        });
    }

    public getBetaaltransactie(transactieId: string, toegangscode?: string): Observable<Betaaltransactie> {
        let url = `${this.baseUrl}/betaaltransacties/${transactieId}`;
        if (toegangscode) {
            url += `?toegangscode=${toegangscode}`;
        }
        return this.httpClient.get<Betaaltransactie>(url, { headers: this.getApiXHeader() });
    }

    public updateBetaaltransactie(transactieId: string, toegangscode: string): Observable<void> {
        return this.httpClient.patch<void>(
            `${this.baseUrl}/betaaltransacties/${transactieId}`,
            { toegangscode },
            { headers: this.getApiXHeader() }
        );
    }

    public isZakelijk(): Observable<IsZakelijk> {
        return this.mijnCacheService.getData<IsZakelijk>(`${this.baseUrl}`, 'is-zakelijk', undefined, undefined, this.getApiXHeader());
    }

    public getPermissies(): Observable<Permissies> {
        return this.mijnCacheService.getData<Permissies>(`${this.baseUrl}`, 'permissies', undefined, undefined, this.getApiXHeader());
    }

    public getLimieten(): Observable<Limiet> {
        const url = `${this.baseUrl}/limieten`;
        return this.httpClient.get<Limiet>(url, { headers: this.getApiXHeader() });
    }

    private getBetaalverzoekByUrl(url: string): Observable<Betaalverzoek> {
        return this.httpClient.get<Betaalverzoek>(url, { headers: this.getApiXHeader() });
    }

    private getApiXHeader(): HttpHeaders {
        return new HttpHeaders().set('X-Api-Key', '{<BSPC-BETAALVERZOEK-API-KEY>}');
    }
}
